/**
 * Classes do "Seu Foco Agora" que precisam valer fora da árvore da home —
 * o header e o rodapé são irmãos do `router-view`, então não há ancestral Vue em comum.
 *
 * Quase todas valem enquanto quem as liga está montado, e saem junto — a exceção é
 * `SEU_FOCO_AGORA_MENU_CLASS`, ligada no `App.vue` porque precisa atravessar a navegação.
 * Mantenha em sincronia com os seletores de `seuFocoAgoraLayout.scss`.
 */

import { scrollAppToTop } from '@/helpers/scrollAppToTop';

/** Reserva o espaço da barra inferior. Quem liga é a home, enquanto está montada. */
export const SEU_FOCO_AGORA_NAV_CLASS = 'seu-foco-agora-nav';

/**
 * Deixa o header transparente sobre a faixa do credor, que sobe por baixo dele, e deixa logo,
 * ícones e texto dele brancos. Quem liga é o próprio bloco, que só existe com a faixa.
 */
export const SEU_FOCO_AGORA_HERO_CLASS = 'seu-foco-agora-hero';

/**
 * Header no padrão do mobile: kebab e drawer no lugar dos itens soltos. Vale em todas as rotas, e
 * não só na home, para o header não trocar de forma no meio da navegação — por isso quem liga é o
 * `App.vue`, e a decisão sai só da flag do JWT.
 */
export const SEU_FOCO_AGORA_MENU_CLASS = 'seu-foco-agora-menu';

/**
 * Onde o menu das abas entra no header, no desktop e sem parcela em foco: sem o bloco colorido em
 * cima, a faixa do menu ficava solta no conteúdo. Mantenha em sincronia com o `id` em `AppHeader.vue`.
 */
export const ALVO_ABAS_HEADER_SEU_FOCO_AGORA = '#app-header-abas';

/** Idempotente: pode ser chamada a cada mudança de estado sem acumular classe. */
export function aplicarNavSeuFocoAgora(ativo: boolean): void {
  document.body.classList.toggle(SEU_FOCO_AGORA_NAV_CLASS, ativo);
}

/** Idempotente, como a de cima. */
export function aplicarMenuSeuFocoAgora(ativo: boolean): void {
  document.body.classList.toggle(SEU_FOCO_AGORA_MENU_CLASS, ativo);
}

/** A instância da faixa que ligou o hero por último: só ela o desliga. */
let instanciaAtiva: symbol | undefined;

/**
 * Idempotente: pode ser chamada a cada mudança de estado sem acumular classe.
 *
 * Quando a cascata troca um card por outro, a faixa nova liga o hero na montagem e o `onUnmounted` da
 * antiga só roda depois. Por isso desligar vale apenas para a faixa que ligou por último (`instancia`):
 * a antiga, já substituída, não apaga a classe da nova.
 */
export function aplicarHeroSeuFocoAgora(instancia: symbol, ativo: boolean): void {
  if (!ativo && instancia !== instanciaAtiva) return;

  instanciaAtiva = ativo ? instancia : undefined;
  document.body.classList.toggle(SEU_FOCO_AGORA_HERO_CLASS, ativo);
}

/** Classe raiz do bloco. É a altura dele que a troca de aba precisa pular. */
const SELETOR_HERO = '.seu-foco-agora';

const FOLGA_ALEM_DO_HERO_PX = 8;

/** WCAG 2.3.3: quem pediu menos movimento ao sistema não leva rolagem animada. */
function prefereMenosMovimento(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function rolarAbaixoDoHeroSeuFocoAgora(): void {
  const hero = document.querySelector(SELETOR_HERO);

  if (hero === null) {
    scrollAppToTop();
    return;
  }

  const alvo = window.scrollY + hero.getBoundingClientRect().bottom + FOLGA_ALEM_DO_HERO_PX;
  window.scrollTo({ top: Math.ceil(alvo), behavior: prefereMenosMovimento() ? 'auto' : 'smooth' });
}
